// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import BoardEditConfirmation from 'app/common/react/components/BoardEdit/BoardEditConfirmation';
import BoardInviteForm from 'app/common/react/components/BoardEdit/BoardInviteForm';
import BoardMergeModalContent from 'app/common/react/components/BoardEdit/BoardMergeModalContent';
import errorUtil from 'app/common/lib/errorUtil';
import ga from 'app/common/lib/ga';
import i18n from 'app/common/lib/i18n';
// eslint-disable-next-line pinterest/no-deprecated-imports
import LegacyModal from 'app/common/react/ui/deprecated/LegacyModal';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import Toast from 'app/common/react/ui/Toast';
import type { AuthViewer } from 'app/packages/pinterest-flow-types/viewer';
import type { BoardPrivacy } from 'app/packages/pinterest-flow-types/board';
import type { FetchingState } from 'app/common/redux/reducers/boards';
import type { State as ReduxState } from 'app/common/redux/reducers/types';
import withShowBlockingActionToast from 'app/common/react/components/save/board/withShowBlockingActionToast';
import withShowToast from 'app/common/lib/react/withShowToast';
import { compose } from 'redux';
import { Experiment } from 'app/common/react/components/experiments';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { track } from 'app/common/lib/react/analytics';
import BoardForm, { type FieldValues } from 'app/common/react/components/BoardForm/BoardForm';
import {
  archiveBoard,
  deleteBoard,
  mergeBoards,
  unarchiveBoard,
  updateBoard,
  type BoardType as BoardActionType,
} from 'app/common/redux/actions/boards';
import { Box, Heading, Text } from 'gestalt';

const GA_CATEGORY_BOARD_EDIT = 'board_edit';
const GA_CATEGORY_BOARD_DELETE = 'board_delete';

export type BoardEditType = {
  access: Array<string>,
  allow_homefeed_recommendations: boolean,
  archived_by_me_at: string,
  category: ?string,
  collaborated_by_me: boolean,
  collaborator_invite_counts: number,
  collaborator_invites_enabled: boolean,
  collaborator_requests_enabled: boolean,
  collab_board_email: boolean,
  description: string,
  id: string,
  image_cover_url: string,
  is_collaborative: boolean,
  name: string,
  owner: {
    full_name: string,
    id: string,
    image_key: string,
    image_medium_url: string,
    username: string,
  },
  pin_count: number,
  privacy: BoardPrivacy,
  type: string,
  url: string,
};

type OwnProps = {|
  board: BoardEditType,
  categories: Array<{
    key: string,
    name: string,
  }>,
  onBoardArchiveChange?: () => void,
  onBoardDelete?: (board: BoardEditType) => void,
  onBoardPrivacyChange?: () => void,
  onBoardLeave?: (boardId: string) => void,
  onBoardUpdate?: (boardId: string, updatedFields: Object) => void,
  onClose: () => void,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  onCropChange?: (imageUrl: string, imagePosition: *) => void,
|};

type Props = {|
  ...OwnProps,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  archiveBoard: () => Promise<*>,
  lastPinnedTo: ?Array<{
    id: string,
    name: string,
  }>,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  deleteBoard: () => Promise<*>,
  fetching: FetchingState,
  fetchAllComplete: boolean,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  mergeToBoard: (targetBoard: { id: string, name: string }) => Promise<*>,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  showToast: (Toast: *) => void,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  unarchiveBoard: () => Promise<*>,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  updateBoard: (board: BoardActionType) => Promise<*>,
  viewer: AuthViewer,
|};

type State = {|
  editMode: 'main' | 'confirmDelete' | 'confirmArchive' | 'confirmUnarchive' | 'merge',
|};

class BoardEdit extends Component<Props, State> {
  state = {
    editMode: 'main',
  };

  handleBoardLeave = () => {
    const { board, onBoardLeave, onClose } = this.props;

    onClose();

    if (onBoardLeave) {
      onBoardLeave(board.id);
    }
  };

  handleArchive = () => {
    const { archiveBoard, onClose } = this.props;
    archiveBoard();
    // optimistically archive
    this.returnToMainEditMode();
    onClose();
  };

  handleUnarchive = () => {
    const { onClose, unarchiveBoard } = this.props;
    unarchiveBoard();
    // optimistically unarchive
    this.returnToMainEditMode();
    onClose();
  };

  returnToMainEditMode = () => this.setState({ editMode: 'main' });

  handleShowArchiveModal = () => this.setState({ editMode: 'confirmArchive' });

  handleShowUnarchiveModal = () => this.setState({ editMode: 'confirmUnarchive' });

  handleShowMergeModal = () => this.setState({ editMode: 'merge' });

  handleDelete = () => {
    const { board, deleteBoard, onBoardDelete, onClose, showToast } = this.props;
    deleteBoard().then(() => {
      showToast(({ onHide }) => (
        <Toast onHide={onHide} text={i18n._('Board deleted!', 'User deleted their board')} />
      ));
    });
    // optimistically treat the board as deleted
    this.returnToMainEditMode();
    onClose();
    if (onBoardDelete) {
      onBoardDelete(board);
    }
  };

  handleMerge = (targetBoard: { id: string, name: string }) => {
    const { board, mergeToBoard, onBoardDelete, onClose } = this.props;
    track(PCONST.thrift.EventType.BOARD_MERGE, { objectId: board.id, merge_flow: 'menu' });
    mergeToBoard(targetBoard);
    // Merging this board into another board is similar to deleting this board
    onClose();
    if (onBoardDelete) {
      onBoardDelete(board);
    }
  };

  handleShowDeleteModal = () => {
    ga.trackEvent(GA_CATEGORY_BOARD_DELETE, PCONST.GA_ACTION_OPEN_MODAL);
    this.setState({ editMode: 'confirmDelete' });
  };

  handleSubmit = (fieldValues: FieldValues) => {
    const { viewer, board, updateBoard, onBoardPrivacyChange, onBoardUpdate, onClose } = this.props;
    const boardId = this.props.board.id;
    if (viewer.id === board.owner.id) {
      const updatedBoard: BoardActionType = { id: boardId, ...fieldValues };
      return updateBoard(updatedBoard).then(() => {
        if (board.privacy !== fieldValues.privacy && onBoardPrivacyChange) {
          onBoardPrivacyChange();
        } else if (onBoardUpdate) {
          onBoardUpdate(boardId, updatedBoard);
        }
        onClose();
      });
    } else {
      const { collab_board_email } = fieldValues;
      const boardSettingsResource = ResourceFactory.create('BoardSettingsResource', {
        board_id: boardId,
        collab_board_email,
      });
      return boardSettingsResource
        .callUpdate({ showError: false })
        .then((response) => onClose())
        .catch((error) => {
          throw error.message;
        });
    }
  };

  render() {
    const { board, categories, viewer, onCropChange, onClose } = this.props;
    const { editMode } = this.state;
    const currentUserIsOwner = viewer.id === board.owner.id;
    const isCollaboratorInvitesEnabled = board.collaborator_invites_enabled;
    const isArchived = !!board.archived_by_me_at;
    const showMergeButton = !isArchived && currentUserIsOwner;
    return (
      <div>
        <Experiment name="archived_boards">
          {({ anyEnabled: showArchiving }) => (
            <BoardForm
              board={board}
              boardCollaboratorsSection={
                currentUserIsOwner || isCollaboratorInvitesEnabled ? (
                  <BoardInviteForm
                    boardId={board.id}
                    boardOwner={board.owner}
                    currentUserIsOwner={currentUserIsOwner}
                    onBoardLeave={this.handleBoardLeave}
                  />
                ) : null
              }
              categories={categories}
              currentUserIsOwner={currentUserIsOwner}
              gaCategory={GA_CATEGORY_BOARD_EDIT}
              isArchivable={showArchiving}
              isDeletable={currentUserIsOwner}
              isEditable={currentUserIsOwner}
              onClose={onClose}
              onCropChange={onCropChange}
              onArchiveButtonClick={this.handleShowArchiveModal}
              onDeleteButtonClick={this.handleShowDeleteModal}
              onMergeButtonClick={showMergeButton ? this.handleShowMergeModal : undefined}
              onSubmit={this.handleSubmit}
              onUnarchiveButtonClick={this.handleShowUnarchiveModal}
              submitText={i18n._('Save')}
              title={
                <Box alignItems="baseline" display="flex">
                  <Box deprecatedMargin={{ right: 1 }}>
                    <Heading size="xs">{i18n._('Edit your board')}</Heading>
                  </Box>
                </Box>
              }
            />
          )}
        </Experiment>
        {editMode !== 'main' && (
          <LegacyModal className="ConfirmDialog">
            {editMode === 'merge' && (
              <Box color="white" shape="rounded" width={440}>
                <BoardMergeModalContent
                  onCancel={this.returnToMainEditMode}
                  onSubmit={this.handleMerge}
                  sourceBoardId={board.id}
                />
              </Box>
            )}
            {editMode === 'confirmDelete' && (
              <BoardEditConfirmation
                cancelButtonLabel={i18n._(
                  'Delete forever',
                  'Confirms the user wants to permanently delete the board',
                )}
                confirmButtonLabel={i18n._('Cancel')}
                message={i18n._("Once you delete a board and all its Pins, you can't undo it!")}
                onCancel={this.handleDelete}
                onConfirm={this.returnToMainEditMode}
              />
            )}
            {editMode === 'confirmArchive' && (
              <BoardEditConfirmation
                confirmButtonLabel={i18n._('Archive', 'button label on confirming board archive')}
                heading={i18n._(
                  'Archive this board?',
                  'modal heading for board archive confirmation',
                )}
                message={interpolateNamedTemplate(
                  i18n._(
                    "If you archive {{ boardName }}, we'll hide it from your profile and you won't be able " +
                      'to save new Pins to it. If you change your mind, you can unarchive it.',
                  ),
                  {
                    boardName: (
                      <Text bold key={board.name} inline>
                        {board.name}
                      </Text>
                    ),
                  },
                )}
                onCancel={this.returnToMainEditMode}
                onConfirm={this.handleArchive}
              />
            )}
            {editMode === 'confirmUnarchive' && (
              <BoardEditConfirmation
                confirmButtonLabel={i18n._(
                  'Unarchive',
                  'button label on confirming board unarchive',
                )}
                heading={i18n._(
                  'Unarchive this board?',
                  'modal heading for board unarchive confirmation',
                )}
                message={interpolateNamedTemplate(
                  i18n._(
                    "If you unarchive {{ boardName }}, it will show up on your profile and you'll be able to " +
                      'save new Pins to it.',
                  ),
                  {
                    boardName: (
                      <Text bold key={board.name} inline>
                        {board.name}
                      </Text>
                    ),
                  },
                )}
                onCancel={this.returnToMainEditMode}
                onConfirm={this.handleUnarchive}
              />
            )}
          </LegacyModal>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  ...state.boards,
  viewer: state.viewer,
});

const mapDispatchToProps = (
  // $FlowFixMe: Please add "import type { Dispatch } from 'redux';"
  dispatch: Dispatch<*>,
  ownProps: OwnProps & {
    hideAllToasts: () => void,
    // $FlowFixMe Flow v0.104.0 upgrade supression.
    showToast: (Toast: *) => void,
    showBlockingActionToast: (options: Object) => void,
  },
) => {
  const handleArchiveChangeComplete = (archive: boolean) => () => {
    const { board, onBoardArchiveChange, showToast } = ownProps;
    if (onBoardArchiveChange) {
      onBoardArchiveChange();
    }
    showToast(({ onHide }) => (
      <Toast
        imageUrl={board.image_cover_url}
        onHide={onHide}
        text={interpolateNamedTemplate(
          archive
            ? i18n._('Archived {{ boardName }}', 'Toast text on complete archiving board')
            : i18n._('Unarchived {{ boardName }}', 'Toast text on complete unarchiving board'),
          { boardName: board.name },
        ).join('')}
      />
    ));
  };
  return {
    archiveBoard: () =>
      dispatch(archiveBoard(ownProps.board.id)).then(handleArchiveChangeComplete(true)),
    deleteBoard: () => dispatch(deleteBoard(ownProps.board.id)),
    mergeToBoard: (targetBoard: { id: string, name: string }) =>
      // $FlowFixMe Flow v0.104.0 upgrade supression.
      (dispatch(mergeBoards(ownProps.board.id, targetBoard.id)): Promise<*>)
        .then(() => {
          ownProps.showBlockingActionToast({
            actionType: 'merge',
            board: targetBoard,
          });
        })
        .catch((error) => {
          errorUtil.showError(
            error.message ||
              i18n._('Failed to merge boards', 'Error message when failed to merge boards'),
          );
        }),
    updateBoard: (board: BoardActionType) => dispatch(updateBoard(board)),
    unarchiveBoard: () =>
      dispatch(unarchiveBoard(ownProps.board.id)).then(handleArchiveChangeComplete(false)),
  };
};

export default compose(
  withShowToast,
  withShowBlockingActionToast,
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(BoardEdit);
